import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  useFilters,
  IFilterState,
  IFilterActions,
} from 'components/SearchPage/hooks/useFilters';
import {
  IFilterMakeModelActions,
  IFilterMakeModelState,
  useFiltersMakeModel,
} from 'components/SearchPage/hooks/useFiltersMakeModel';
import {
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageFilter,
} from 'api/types/searchPageApiTypes';
import { ResponseMakeModelType, ResponseModelFilter } from 'types';
import { useLocationContext } from 'features/location/Location.context';

export type SearchFilterStateType = IFilterState &
  IFilterMakeModelState & {
    appliedFiltersRangeCount: number;
    isFilterModalOpen: boolean;
    isAddAnotherEnabled: boolean;
  };
export type SearchFilterDispatchType = IFilterActions &
  IFilterMakeModelActions & {
    setIsFilterModalOpen: (isOpen: boolean) => void;
    getCountOfFiltersApplied: (search: string, sort: string) => number;
    getYears: () => string[] | undefined;
    getFuelTypes: () => string[] | undefined;
    getColours: () => string[] | undefined;
    getBodyTypes: () => string[] | undefined;
    getTransmissions: () => string[] | undefined;
    getAreas: () => string[];
    getMakes: () => string[];
    getModels: () => string[];
    getWanted: () => boolean;
    reset: () => void;
  };

export const SearchFilterDispatchContext = createContext(
  {} as SearchFilterDispatchType,
);

export const SearchFilterStateContext = createContext(
  {} as SearchFilterStateType,
);

export const useSearchFilterDispatch = (): SearchFilterDispatchType =>
  useContext(SearchFilterDispatchContext);
export const useSearchFilterState = (): SearchFilterStateType =>
  useContext(SearchFilterStateContext);

export const SearchFilterProvider = ({
  filtersData,
  filtersMakeModelData,
  filtersQueryParams,
  makeModelParams,
  rangeQueryParams,
  children,
}: {
  filtersData: ISearchPageFilter[];
  filtersMakeModelData: ResponseModelFilter;
  filtersQueryParams: IGetAdsRequestFilter[];
  makeModelParams: ResponseMakeModelType;
  rangeQueryParams: IGetAdsRequestRange[];
  children: ReactElement;
}): ReactElement => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const { state: stateFilters, dispatch: dispatchFilters } = useFilters(
    filtersData,
    filtersQueryParams,
    rangeQueryParams,
  );
  const { state: stateMakeModel, dispatch: dispatchMakeModel } =
    useFiltersMakeModel(filtersData, filtersMakeModelData, makeModelParams);

  const { selectedCounty, resetToDefault } = useLocationContext();

  const resetFilters = useCallback(() => {
    dispatchFilters.resetFilters();
    dispatchMakeModel.resetMakeModel();
    resetToDefault();
  }, []);

  const getCountOfFiltersApplied = useCallback(
    (search: string, sort: string) => {
      let count = 0;
      const makeModelCount = stateMakeModel.makeModel.filter(
        (makeModelGroup) =>
          makeModelGroup.makeValue.length > 0 &&
          makeModelGroup.makeValue !== 'All Makes' &&
          makeModelGroup.makeValue !== '',
      ).length;
      const locationCount = selectedCounty?.value ? 1 : 0;

      count += locationCount;
      count += stateFilters.appliedFiltersRangeCount;
      count += makeModelCount;
      count += search ? 1 : 0;
      count += sort ? 1 : 0;
      return count;
    },
    [
      stateFilters.appliedFiltersRangeCount,
      stateMakeModel.makeModel,
      selectedCounty,
    ],
  );

  function getYears(): string[] | undefined {
    const yearRange = dispatchFilters.getRangeByName(
      stateFilters.ranges,
      'year',
    );
    if (!!yearRange?.to && yearRange.to === yearRange?.from) {
      return [yearRange?.to];
    }
    return;
  }

  function getFuelTypes(): string[] | undefined {
    const fuelFilter = dispatchFilters.getFilterByName(
      stateFilters.filters,
      'fuelType',
    );
    return fuelFilter?.values.length ? fuelFilter?.values : undefined;
  }

  function getColours(): string[] | undefined {
    const colourFilter = dispatchFilters.getFilterByName(
      stateFilters.filters,
      'colour',
    );
    return colourFilter?.values.length ? colourFilter?.values : undefined;
  }

  function getBodyTypes(): string[] | undefined {
    const bodyTypeFilter = dispatchFilters.getFilterByName(
      stateFilters.filters,
      'bodyType',
    );
    return bodyTypeFilter?.values.length ? bodyTypeFilter.values : undefined;
  }

  function getTransmissions(): string[] | undefined {
    const transmissionFilter = dispatchFilters.getFilterByName(
      stateFilters.filters,
      'transmission',
    );
    return transmissionFilter?.values.length
      ? transmissionFilter.values
      : undefined;
  }

  function getAreas(): string[] {
    return (
      dispatchFilters.getFilterByName(stateFilters.filters, 'area')?.values ||
      []
    );
  }

  function getMakes(): string[] {
    const makes = dispatchMakeModel.getMakeValues();
    if (makes.length > 0) {
      return makes;
    } else {
      return (
        dispatchFilters.getFilterByName(stateFilters.filters, 'make')?.values ||
        []
      );
    }
  }

  function getModels(): string[] {
    const models = dispatchMakeModel.getModelValues();
    if (models) {
      return models;
    }
    return [];
  }

  function getWanted(): boolean {
    return (
      dispatchFilters.getFilterByName(stateFilters.filters, 'adType')
        ?.values[0] === 'wanted'
    );
  }

  return (
    <SearchFilterDispatchContext.Provider
      value={{
        setIsFilterModalOpen,
        getCountOfFiltersApplied,
        getYears,
        getFuelTypes,
        getColours,
        getBodyTypes,
        getTransmissions,
        getAreas,
        getMakes,
        getModels,
        getWanted,
        ...dispatchMakeModel,
        ...dispatchFilters,
        reset: resetFilters,
      }}
    >
      <SearchFilterStateContext.Provider
        value={{
          ...stateFilters,
          ...stateMakeModel,
          isFilterModalOpen,
        }}
      >
        {children}
      </SearchFilterStateContext.Provider>
    </SearchFilterDispatchContext.Provider>
  );
};
